import React, { useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Pencil, Check, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { formatCurrency, formatDateDMY } from '../../lib/utils';
import { contratosApi } from '../../lib/api';

const IVA_LABEL = {
  responsable_inscripto: 'Responsable Inscripto', monotributo: 'Monotributo', monotributista: 'Monotributo',
  exento: 'Exento', consumidor_final: 'Consumidor Final',
};

const Dato = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-400">{label}</p>
    <p className="text-slate-700">{value || '—'}</p>
  </div>
);

const DetalleContrato = ({ c }) => {
  const indices = (c.ajuste_indices || []).map(i => `${(i.tipo || '').toUpperCase()}${i.peso ? ` ${i.peso}%` : ''}`).join(' + ')
    || (c.indice_tipo || '').toUpperCase();
  return (
    <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`contrato-finalizado-detalle-${c.contrato_id}`}>
      <Dato label="Inquilino" value={c.inquilino} />
      <Dato label="Razón social" value={c.razon_social} />
      <Dato label="CUIT" value={c.cuit} />
      <Dato label="Condición IVA" value={IVA_LABEL[c.condicion_iva] || c.condicion_iva} />
      <Dato label="Monto inicial" value={c.monto_inicial != null ? formatCurrency(c.monto_inicial, c.moneda) : null} />
      <Dato label="Último monto" value={c.monto_actual != null ? formatCurrency(c.monto_actual, c.moneda) : null} />
      <Dato label="Ajuste" value={[indices, c.ajuste_frecuencia || c.indice_frecuencia].filter(Boolean).join(' · ')} />
      <Dato label="Día de vencimiento" value={c.dia_vencimiento} />
      <Dato label="Email facturación" value={(c.emails_facturacion || [])[0] || c.email_facturacion} />
      <Dato label="Contacto administración" value={[c.contacto_admin_nombre, c.contacto_admin_email].filter(Boolean).join(' · ')} />
      <Dato label="Contacto comercial" value={[c.contacto_comercial_nombre, c.contacto_comercial_email].filter(Boolean).join(' · ')} />
      <Dato label="Domicilio fiscal" value={c.domicilio_fiscal} />
    </div>
  );
};

const FechaBajaEditor = ({ c, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [valor, setValor] = useState((c.fecha_baja || '').slice(0, 10));
  const [saving, setSaving] = useState(false);

  const guardar = async () => {
    if (!valor) return toast.error('Elegí una fecha de baja');
    setSaving(true);
    try {
      await contratosApi.editarFechaBaja(c.contrato_id, valor);
      toast.success('Fecha de baja actualizada. La facturación se ajusta a esta fecha.');
      onSaved(valor);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar la fecha de baja');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <span className="flex items-center gap-1">
        <span className="text-slate-500">Baja:</span>
        <span className="font-medium text-slate-700" data-testid={`contrato-finalizado-fecha-baja-${c.contrato_id}`}>
          {c.fecha_baja ? formatDateDMY(c.fecha_baja) : 'sin fecha'}
        </span>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditing(true)}
          data-testid={`contrato-finalizado-editar-baja-${c.contrato_id}`} title="Editar fecha de baja">
          <Pencil className="w-3.5 h-3.5" />
        </Button>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <span className="text-slate-500">Baja:</span>
      <Input type="date" value={valor} onChange={e => setValor(e.target.value)} className="h-7 w-40"
        data-testid={`contrato-finalizado-input-baja-${c.contrato_id}`} />
      <Button size="icon" className="h-7 w-7" onClick={guardar} disabled={saving}
        data-testid={`contrato-finalizado-guardar-baja-${c.contrato_id}`}>
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(false)}
        data-testid={`contrato-finalizado-cancelar-baja-${c.contrato_id}`}>
        <X className="w-3.5 h-3.5" />
      </Button>
    </span>
  );
};

export const ContratoFinalizadoCard = ({ contrato }) => {
  const [c, setC] = useState(contrato);
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-slate-50 rounded-lg p-3 text-sm" data-testid={`contrato-finalizado-${c.contrato_id}`}>
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium">{c.nombre_comercial || c.razon_social || c.inquilino || 'Sin nombre'}</span>
          <span className="ml-2 text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full">{c.estado}</span>
        </div>
        <span className="text-slate-500">Nro: {c.numero_contrato}</span>
      </div>
      <div className="mt-1 text-slate-500 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span>{c.fecha_inicio ? formatDateDMY(c.fecha_inicio) : '?'} → {c.fecha_fin ? formatDateDMY(c.fecha_fin) : '?'}</span>
        {c.monto_inicial ? <span>Monto: {formatCurrency(c.monto_inicial, c.moneda)}</span> : null}
        <FechaBajaEditor c={c} onSaved={(fb) => setC(prev => ({ ...prev, fecha_baja: fb }))} />
        <Button variant="link" size="sm" className="h-6 px-0 ml-auto" onClick={() => setOpen(o => !o)}
          data-testid={`contrato-finalizado-ver-detalle-${c.contrato_id}`}>
          {open ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
          {open ? 'Ocultar detalle' : 'Ver detalle'}
        </Button>
      </div>
      {open && <DetalleContrato c={c} />}
    </div>
  );
};
